import { Button } from "./ui/button";
import {
  ShoppingBasket,
  CheckCircle2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useBasket } from "../context/BasketContext";
import { useLanguage } from "../context/LanguageContext";
import { formatHebrewRef, isTalmudSlug, talmudDafLabel } from "../lib/hebrewRef";
import { toast } from "sonner";

/**
 * ChapterActionsMenu — Simplified single-action button to add whole chapter to basket.
 *
 * Replaces the complex dropdown menu with a clear, direct action:
 *   [ הוסף פרק שלם לסל ]  /  [ Add full chapter to basket ]
 *
 * When clicked, adds all verses in the current chapter to the Lesson Basket
 * and shows a success toast notification. Text is fully i18n-aware; Hebrew
 * copy is preserved byte-identically in he.json (key parity).
 */
export default function ChapterActionsMenu({
  book,            // english slug
  bookHe,
  chapter,
  verses,          // [{verse, he, en}]
}) {
  const basket = useBasket();
  const { t } = useTranslation();
  const { isEn } = useLanguage();
  const isTalmud = isTalmudSlug(book);

  // Unit labels — "פרק" / "Chapter" or "דף" / "Daf" depending on the corpus
  // and the active UI language.
  const unitHe = isTalmud ? "דף" : "פרק";
  const unitEn = isTalmud ? "Daf" : "Chapter";
  const unitWord = isEn ? unitEn : unitHe;

  // Refer to the current chapter using the corpus-appropriate label
  // ("דף ב׳ ע״א" for the Talmud, the standard "פרק <gematria>" otherwise)
  // so the toast and button copy don't leak the wrong unit. In EN we
  // use Arabic numerals to match the rest of the EN chrome.
  const refHe = isTalmud
    ? `${bookHe || book} ${talmudDafLabel(chapter)}`
    : formatHebrewRef(bookHe || book, chapter, undefined, "chapter-only");
  const refEn = isTalmud
    ? `${book} ${talmudDafLabel(chapter)}`
    : `${book} · Chapter ${chapter}`;
  const ref = isEn ? refEn : refHe;

  const verseCount = Array.isArray(verses) ? verses.length : 0;

  // Track whether the chapter is already in basket
  const isChapterInBasket = basket.items.some(
    (it) => it.type === "chapter" && it.book === book && it.chapter === chapter
  );

  const addChapterToBasket = () => {
    if (!verses || verses.length === 0) {
      toast.info(t("chapter_actions.toast_waiting_title"), {
        description: t("chapter_actions.toast_waiting_desc"),
        duration: 2000,
      });
      return;
    }

    if (isChapterInBasket) {
      toast.message(
        t("chapter_actions.toast_already_in_basket", { unit: unitWord })
      );
      return;
    }

    // Add ONLY the verses individually - no chapter marker
    // This ensures the lesson builder can process them properly
    verses.forEach((v) => {
      basket.add({
        type: "verse",
        book,
        book_he: bookHe,
        chapter,
        verse: v.verse,
        text_he: v.he,
        text_en: v.en || "",
      });
    });

    // Show clear success message
    toast.success(
      t("chapter_actions.toast_added_title", { ref }),
      {
        description: t(
          isTalmud
            ? "chapter_actions.toast_added_desc_talmud"
            : "chapter_actions.toast_added_desc_tanakh",
          { count: verseCount }
        ),
        duration: 4000,
      }
    );
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={addChapterToBasket}
      data-testid="chapter-add-to-basket"
      data-chapter-in-basket={isChapterInBasket ? "true" : "false"}
      className={`h-8 px-3 gap-2 font-heading text-xs transition-all ${
        isChapterInBasket
          ? "text-gold border border-gold/40 bg-gold/10"
          : "text-foreground hover:text-gold hover:bg-gold/10 border border-border/40 hover:border-gold/40"
      }`}
      title={
        isChapterInBasket
          ? t("chapter_actions.title_already_in_basket", { unit: unitWord })
          : t("chapter_actions.title_add_to_basket", { unit: unitWord })
      }
    >
      {isChapterInBasket ? (
        <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
      ) : (
        <ShoppingBasket className="h-4 w-4" strokeWidth={1.5} />
      )}
      <span className="hidden sm:inline">
        {isChapterInBasket
          ? t("chapter_actions.label_in_basket", { unit: unitWord })
          : t("chapter_actions.label_add_to_basket", { unit: unitWord })}
      </span>
    </Button>
  );
}
